import { t } from 'i18next';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { companyActions } from '../../../modules/company';
import { modalsActions, modalsConstants } from '../../../modules/modals';
import useDetectDiffInChanges from '../../../utils/hooks/useDetectDiffInChanges';
import useWindowSize from '../../../utils/hooks/useWindowSize';
import PhotosBlueArrow from '../../assets/imgs/icons/PhotosBlueArrow.svg';
import { SCREEN_SIZES } from '../../constants/screenSizes';
import { FieldError, Icon, Text } from '../common';
import ConfirmationModal from '../modals/confirmation';

import FieldComment from './common/fieldComment';
import SaveBtn from './common/saveBtn';
import {
  PicturesSettingsWrS,
  PicturesSettingsItemS,
  PicturesSettingsFileS,
  PicturesSettingsViewS,
  PicturesSettingsRemoveS,
  PicturesSettingsRemoveWrS,
  PicturesSettingsRemoveIconS,
  PicturesSettingsSingleWrapper,
  MultipleMobileImagesEditWrapper,
  MultipleMobileImagesHeader,
  MultipleMobileImagesEditSectionWrapper,
  MobilePicturesSettingsViewWrapper,
  DeleteImageIcon,
} from './companyEditS';

const { uploadCompanyPicture, removeCompanyPicture } = companyActions;

const MAX_IMAGES_ALLOWED = 10;

const PicturesSettingsMobile = ({ images, onPictureChoose, handleDeleteModal }) =>
  images.length === 0 ? (
    <PicturesSettingsSingleWrapper>
      <PicturesSettingsItemS active>
        <PicturesSettingsFileS
          type="file"
          accept=".png, .jpg, .jpeg"
          disabled={images.length >= MAX_IMAGES_ALLOWED}
          onChange={onPictureChoose}
          multiple
        />
      </PicturesSettingsItemS>
    </PicturesSettingsSingleWrapper>
  ) : (
    <MultipleMobileImagesEditSectionWrapper>
      <MultipleMobileImagesHeader>
        <Text>
          <Trans i18nKey="companyEdit.pictures.addPhotos" />
        </Text>
        <Icon src={PhotosBlueArrow} />
      </MultipleMobileImagesHeader>
      <MultipleMobileImagesEditWrapper>
        {images.length < MAX_IMAGES_ALLOWED && (
          <PicturesSettingsItemS active className="add-image">
            <PicturesSettingsFileS
              type="file"
              accept=".png, .jpg, .jpeg"
              disabled={images.length >= MAX_IMAGES_ALLOWED}
              onChange={onPictureChoose}
              multiple
            />
          </PicturesSettingsItemS>
        )}
        {images.map((image, index) => (
          <MobilePicturesSettingsViewWrapper key={index} imageNumber={`${index + 1}`}>
            <PicturesSettingsItemS active={images.length === index}>
              <DeleteImageIcon onClick={() => handleDeleteModal(image)} />
              <PicturesSettingsViewS src={image} />
            </PicturesSettingsItemS>
          </MobilePicturesSettingsViewWrapper>
        ))}
      </MultipleMobileImagesEditWrapper>
    </MultipleMobileImagesEditSectionWrapper>
  );

const PicturesSettingsDesktop = ({ images, onPictureChoose, handleDeleteModal }) =>
  Array.from({ length: MAX_IMAGES_ALLOWED }).map((_, index) => {
    const image = images[index];
    return (
      <PicturesSettingsItemS key={index} active={images.length === index}>
        {!image && (
          <PicturesSettingsFileS
            type="file"
            accept=".png, .jpg, .jpeg"
            disabled={images.length >= MAX_IMAGES_ALLOWED}
            onChange={onPictureChoose}
            multiple
          />
        )}
        {image && <PicturesSettingsViewS src={image} />}
        {image && (
          <PicturesSettingsRemoveS>
            <PicturesSettingsRemoveWrS onClick={() => handleDeleteModal(image)}>
              <PicturesSettingsRemoveIconS icon="trash-alt" />
            </PicturesSettingsRemoveWrS>
          </PicturesSettingsRemoveS>
        )}
      </PicturesSettingsItemS>
    );
  });

const PicturesSettings = ({ setActiveSettingType }) => {
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [imagesToUpload, setImagesToUpload] = useState([]);
  const [imageSizeError, setImageSizeError] = useState(false);
  const { width: windowWidth } = useWindowSize();

  const company = useSelector((state) => state.company.companyItem);
  const loading = useSelector((state) => state.company.loading);
  const dispatch = useDispatch();

  const images = [...(company.pictures || []), ...imagePreviewUrls];

  const hasChanges = () => imagesToUpload.length > 0;

  const savePictures = () => {
    if (imagesToUpload.length) {
      const formDataArray = imagesToUpload.map((file) => {
        const formData = new FormData();
        formData.append('picture', file);
        return formData;
      });

      dispatch(
        uploadCompanyPicture(company.id, formDataArray, () => {
          setImagePreviewUrls([]);
          setImagesToUpload([]);
        })
      );
      setImageSizeError(false);
    }
  };

  useDetectDiffInChanges(hasChanges, savePictures, setActiveSettingType);

  const readFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => setImagePreviewUrls((prev) => [...prev, reader.result]);
    reader.readAsDataURL(file);

    setImagesToUpload((prev) => [...prev, file]);
  };

  const processFiles = (files) => {
    const MAX_SIZE_FILE = 1024 * 1024 * 5;
    let sizeError = false;

    files.forEach((file) => {
      if (file.type.match('image.*') && file.size < MAX_SIZE_FILE) {
        readFile(file);
      } else {
        sizeError = true;
      }
    });

    setImageSizeError(sizeError);
  };

  const onPictureChoose = (event) => {
    const filesToProcess = Array.from(event.target.files).slice(0, MAX_IMAGES_ALLOWED - images.length);

    processFiles(filesToProcess);
    event.target.value = null;
  };

  const handleDeleteModal = (pictureUrl) => {
    dispatch(
      modalsActions.setModalStateFor(modalsConstants.MODAL_TYPES.DELETE_PICTURE_COMPANY, {
        open: true,
        id: company.id,
        pictureUrl,
      })
    );
  };

  const handleImageDelete = (id, pictureUrl) => {
    setImagePreviewUrls((prev) => prev.filter((url) => url !== pictureUrl));
    setImagesToUpload((prev) => prev.filter((_, i) => i !== pictureUrl));
    dispatch(removeCompanyPicture({ id, pictureUrl }));
  };

  return (
    <>
      <ConfirmationModal
        confirmationName={modalsConstants.MODAL_TYPES.DELETE_PICTURE_COMPANY}
        onApproved={(id, { pictureUrl }) => handleImageDelete(id, pictureUrl)}
      />
      {windowWidth > SCREEN_SIZES.MEDIUM && (
        <FieldComment transPath="companyEdit.fieldComment.pictures" className="angle-down" />
      )}
      <PicturesSettingsWrS>
        {windowWidth <= SCREEN_SIZES.MEDIUM ? (
          <PicturesSettingsMobile
            images={images}
            onPictureChoose={onPictureChoose}
            handleDeleteModal={handleDeleteModal}
          />
        ) : (
          <PicturesSettingsDesktop
            images={images}
            onPictureChoose={onPictureChoose}
            handleDeleteModal={handleDeleteModal}
          />
        )}
        {imageSizeError && <FieldError error={t('companyEdit.errorMessages.fileSize')} />}
        <div style={{ marginTop: '30px' }}>
          <SaveBtn loading={loading} onClick={savePictures} disabled={!hasChanges()} />
        </div>
      </PicturesSettingsWrS>
    </>
  );
};

export default PicturesSettings;
